import Tooltip from '@/components/common/Tooltip';
import DetailIcon from '@/components/icon/DetailIcon';
import Table from '@/components/table/Table';
import { DISPPLAY_MOMENT_YMD_HM, ORDER_STATUS_CMS } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { TransactionHistoryItem } from '@/types/reservation';
import { formatDateTime, isValidYYYYMMDDHHmmss } from '@/utils/datetime';
import { formatCurrency } from '@/utils/formatNumber';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface TransactionHistoryProps {
    orderId: number
}
export default observer(function TransactionHistory({ orderId }: TransactionHistoryProps) {
    // hooks
    const { t } = useTranslation();
    // store
    const { reservationStore: { transactions, reservationTransactionInfor, transactionPaging, totalTransactionRecord, getTransactionHistory, cleanTransaction } } = useStore();

    // lifecycle
    useEffect(() => {
        if (orderId) {
            onFetch();
        }

        return () => {
            cleanTransaction();
        };
    }, [orderId]);
    // function

    const onFetch = (searchParams?: any, paging?: TableState) => {
        let params = { ...searchParams, id: orderId };
        getTransactionHistory(params, paging);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(transactionPaging) : stateUpdater;
        onFetch(null, newPaging);
    };
    const columns: ColumnDef<TransactionHistoryItem>[] = [
        // {
        //     id: 'id',
        //     accessorKey: 'id',
        //     header: t('field.stt'),
        //     size: 20,
        //     enableSorting: false,
        //     cell: ({ row }) => {
        //         const { pageIndex, pageSize } = transactionPaging.pagination;
        //         const index = (row.index + 1) + (pageIndex * pageSize);
        //         return index;
        //     }
        // },
        {
            accessorKey: 'orderId',
            header: t('field.transaction_code'),
            size: 120,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return <div>{original?.orderId}</div>;
            }
        },
        {
            accessorKey: 'date',
            header: t('field.transaction_date'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return (
                    <div>
                        {original?.processDate && (
                            isValidYYYYMMDDHHmmss(original?.processDate) ? formatDateTime(moment(original?.processDate, 'YYYYMMDDHHmmss').toDate(), DISPPLAY_MOMENT_YMD_HM, { useLocalTime: true }) : formatDateTime(original?.processDate, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: true })
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: 'method',
            header: t('field.reservation.payment_method'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return <div className='whitespace-nowrap'>{original?.paymentMethod}</div>;
            }
        },
        {
            accessorKey: 'status',
            header: t('field.status'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return <div>{original?.result}</div>;
            }
        },
        {
            accessorKey: 'amount',
            header: t('field.amount'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return (
                    <div>
                        {`${t('currency.currency_symbol')}`}
                        {formatCurrency(original?.amount || 0)}
                    </div>
                );
            }
        },
        {
            accessorKey: 'infor',
            header: '',
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                let content: ReactNode = <></>;
                if (original.info) {
                    let infor = original.info;
                    content = (
                        <>
                            {Object.keys(infor).filter(key => infor[key]).map((key) => {
                                return (
                                    <div key={key} className='flex gap-2'>
                                        <label>
                                            {key}
                                            :
                                        </label>
                                        <p className='font-medium'>{infor[key]}</p>
                                    </div>
                                );
                            })}
                        </>
                    );
                }

                return (
                    <div className='w-full flex justify-center'>
                        <Tooltip content={content}>
                            <div
                                // type='button'
                                className='cursor-pointer w-fit h-fit'
                            >
                                <div className='relative inline-block group'>
                                    {/* <div className='absolute inset-0 bg-transparent group-hover:bg-gray-300 transition-colors z-[-1] rounded-full' /> */}
                                    <DetailIcon width={20} height={20} />
                                </div>
                            </div>
                        </Tooltip>
                    </div>
                );
            }
        }
    ];

    return (
        <div className='flex flex-col gap-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                <div className='flex gap-2'>
                    <label>
                        {t('field.reservation_code')}
                        :
                        {' '}
                    </label>
                    <p className='font-medium'>{reservationTransactionInfor?.reservationCode}</p>
                </div>
                <div className='flex gap-2'>
                    <label>
                        {t('field.reservation.reservation_status')}
                        :
                        {' '}
                    </label>
                    {
                        reservationTransactionInfor?.status != null ?
                            (
                                <div className={classNames(`${ORDER_STATUS_CMS[reservationTransactionInfor?.status]?.color}`, 'font-medium')}>
                                    {t(`${ORDER_STATUS_CMS[reservationTransactionInfor?.status]?.key}`)}
                                </div>
                            ) :
                            <p>--</p>
                    }

                </div>
            </div>
            <div className='mt-2 relative'>
                <Table
                    columns={columns}
                    data={transactions}
                    state={{
                        ...transactionPaging
                    }}
                    showSelectPageSize={false}
                    rowCount={totalTransactionRecord}
                    onStateChange={onStateChange}
                    getRowId={row => row.id?.toString()}
                    enableScrollToTopTable={false}
                />
            </div>
        </div>
    );
});
